import { memo, type Ref, useCallback, useRef } from 'react'

import type {
  TypeaheadHandle,
  TypeaheadProps,
} from '@fv/client-components/combobox'

import {
  boldSearchText,
  defaultGetOptionLabel,
  type ShipperClassNames,
  ShipperComboBox,
  type ShipperInternalKeys,
  shipperInputClassNames,
} from './ShipperComboBox'

type Props<T> = Omit<
  TypeaheadProps<T>,
  ShipperInternalKeys | 'getOptionLabel'
> & {
  ref?: Ref<TypeaheadHandle>
  getOptionLabel?: (option: T) => string
  error?: string
  classNames?: ShipperClassNames<T>
  showTrigger?: boolean
}

function ShipperTypeaheadImpl<T>({
  ref,
  error,
  classNames,
  getOptionLabel = defaultGetOptionLabel,
  onInputValueChange,
  renderOption,
  name,
  value,
  showTrigger,
  ...rest
}: Props<T>) {
  const searchTextRef = useRef('')

  const handleInputValueChange = useCallback(
    (val: string) => {
      searchTextRef.current = val
      onInputValueChange?.(val)
    },
    [onInputValueChange],
  )

  const defaultRenderOption = useCallback(
    (option: T) =>
      boldSearchText(getOptionLabel(option), searchTextRef.current),
    [getOptionLabel],
  )

  return (
    <div className={classNames?.container} data-cy={name}>
      <ShipperComboBox<T>
        ref={ref}
        name={name}
        value={value}
        onSelect={rest.onSelect}
        onInputValueChange={handleInputValueChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption ?? defaultRenderOption}
        showTrigger={showTrigger}
        classNames={{
          ...shipperInputClassNames,
          root: `${shipperInputClassNames.root}${showTrigger ? ' relative pr-8' : ''}`,
          ...classNames,
        }}
        {...rest}
      />
    </div>
  )
}

export const ShipperTypeahead = memo(
  ShipperTypeaheadImpl,
) as typeof ShipperTypeaheadImpl
